import { useMemo } from 'react'

import { REPLAY_EVENTS } from '../utils/replayAnnotations'
import { changeSteps } from '../utils/replayChanges'

// Latest visible payload of an adapter-added event (see replayAnnotations.js), or null.
const latestPayload = (visibleEvents, type) => {
  for (let i = visibleEvents.length - 1; i >= 0; i--) {
    if (visibleEvents[i].type === type) return visibleEvents[i].payload || null
  }
  return null
}

export function useReplayState(events, currentTime, startTime, starterCode = '') {
  return useMemo(() => {
    // Get all events up to current time
    const targetTime = startTime + currentTime
    const visibleEvents = events.filter(e => e.timestamp <= targetTime)

    // Mouse position - find latest MOUSE_MOVE or MOUSE_CLICK
    const lastMouseEvent = [...visibleEvents]
      .reverse()
      .find(e => e.type === 'MOUSE_MOVE' || e.type === 'MOUSE_CLICK')
    
    const mousePos = lastMouseEvent?.payload 
      ? { x: lastMouseEvent.payload.x, y: lastMouseEvent.payload.y }
      : { x: 0.5, y: 0.5 }

    // Click state - check if there was a recent click (within 200ms)
    const lastClickEvent = [...visibleEvents]
      .reverse()
      .find(e => e.type === 'MOUSE_CLICK')
    
    const isClicking = lastClickEvent 
      ? (targetTime - lastClickEvent.timestamp) < 200
      : false

    // Code state - find latest event carrying a full code snapshot. Not restricted to a
    // 'CODE_' type prefix: CODE_TYPE/CODE_DELETE etc. only ever carry per-keystroke deltas,
    // never a full snapshot - the events that actually have one (TASK_COMPLETED/TASK_TIMEOUT,
    // and every TERMINAL_RUN as of the TerminalContext.js fix) don't share that prefix, so
    // this filter previously matched nothing and replay always fell back to starterCode.
    const lastCodeEvent = [...visibleEvents]
      .reverse()
      .find(e => e.payload?.code !== undefined)
    
    const code = lastCodeEvent?.payload?.code ?? starterCode
    // Which editor tab (html/css/js) was actually on screen at this point in the replay -
    // CodeEditor.jsx shows one file at a time via tabs, never all three at once, so the
    // replay needs to track this to show the same thing the student actually saw.
    const activeFile = lastCodeEvent?.payload?.active_file

    // InitialFinalCSS's IDE/"Your View" divider position at this point in the replay - see
    // usePanelResize.js's 'ideSplit' resizer, logged as a PANEL_RESIZE event. Defaults to
    // 50 (the same default the live workspace starts at) until the student first drags it.
    const lastIdeSplitEvent = [...visibleEvents]
      .reverse()
      .find(e => e.type === 'PANEL_RESIZE' && e.payload?.panel === 'ideSplit')
    const ideSplitPercent = lastIdeSplitEvent?.payload?.newSize ?? 50

    // InitialFinalJS's Target/"Your View" divider position - same reconstruction as
    // ideSplitPercent above, see usePanelResize.js's 'jsViewSplit' resizer.
    const lastJsViewSplitEvent = [...visibleEvents]
      .reverse()
      .find(e => e.type === 'PANEL_RESIZE' && e.payload?.panel === 'jsViewSplit')
    const jsViewSplitPercent = lastJsViewSplitEvent?.payload?.newSize ?? 50

    // Terminal state - accumulate output since last TERMINAL_RUN (or an adapter's
    // TERMINAL_CLEAR, for pages that empty their output panel). terminalLine/stream payload
    // fields only come from adapters; workspace events never set them.
    const terminalEvents = visibleEvents.filter(e => 
      ['TERMINAL_RUN', 'TERMINAL_OUTPUT', 'TERMINAL_ERROR', 'TEST_CASE_RESULT', REPLAY_EVENTS.TERMINAL_CLEAR].includes(e.type)
    )
    
    let terminalOutput = []
    let isRunning = false

    const lastRunIndex = terminalEvents.map(e => e.type === REPLAY_EVENTS.TERMINAL_CLEAR ? 'TERMINAL_RUN' : e.type).lastIndexOf('TERMINAL_RUN')
    
    if (lastRunIndex !== -1) {
      const eventsFromLastRun = terminalEvents.slice(lastRunIndex)
      
      for (const event of eventsFromLastRun) {
        switch (event.type) {
          case 'TERMINAL_RUN': {
            isRunning = true
            const line = event.payload?.terminalLine ?? '> Running tests...'
            terminalOutput = line ? [{ type: 'stdout', content: line }] : []
            break
          }
          case REPLAY_EVENTS.TERMINAL_CLEAR:
            isRunning = false
            terminalOutput = []
            break
          case 'TERMINAL_OUTPUT':
            isRunning = false
            terminalOutput.push({ 
              type: event.payload?.stream === 'stderr' ? 'stderr' : 'stdout', 
              content: event.payload?.output || '' 
            })
            break
          case 'TERMINAL_ERROR':
            isRunning = false
            terminalOutput.push({ 
              type: 'stderr', 
              content: `${event.payload?.type || 'Error'}: ${event.payload?.message || ''}` 
            })
            break
          case 'TEST_CASE_RESULT':
            isRunning = false
            const { passed_count, total_tests, terminalLine } = event.payload || {}
            if (terminalLine !== '') {
              terminalOutput.push({ 
                type: 'stdout', 
                content: terminalLine ?? `Tests passed: ${passed_count}/${total_tests}` 
              })
            }
            break
          default:
            break
        }
      }
    }

    // Test results - find latest TEST_CASE_RESULT
    const lastTestResult = [...visibleEvents]
      .reverse()
      .find(e => e.type === 'TEST_CASE_RESULT')
    
    let testResults = []
    if (lastTestResult?.payload) {
      const { passed_count, total_tests, detailed_results } = lastTestResult.payload
      
      if (detailed_results) {
        testResults = detailed_results
      } else if (passed_count !== undefined && total_tests !== undefined) {
        for (let i = 0; i < total_tests; i++) {
          testResults.push({
            test_case_number: i + 1,
            passed: i < passed_count
          })
        }
      }
    }

    // Chat state - build messages from CHAT_QUERY and CHAT_RESPONSE
    const chatEvents = visibleEvents.filter(e => 
      ['CHAT_QUERY', 'CHAT_RESPONSE', 'CHAT_TYPE'].includes(e.type)
    )

    const messages = []
    let messageId = 1

    for (const event of chatEvents) {
      if (event.type === 'CHAT_QUERY') {
        messages.push({
          id: messageId++,
          sender: 'user',
          content: event.payload?.text || event.payload?.message || event.payload?.content || '',
          // wallTime: the real time when the timeline was compressed (compressIdleGaps.js).
          timestamp: event.wallTime ?? event.timestamp,
        })
      } else if (event.type === 'CHAT_RESPONSE') {
        messages.push({
          id: messageId++,
          sender: 'ai',
          content: event.payload?.content || '',
          timestamp: event.wallTime ?? event.timestamp,
        })
      }
    }

    // Current input - find latest CHAT_TYPE after the last CHAT_QUERY
    const lastQueryIndex = chatEvents.map(e => e.type).lastIndexOf('CHAT_QUERY')
    const chatTypeEvents = chatEvents.filter(e => e.type === 'CHAT_TYPE')
    
    let currentInput = ''
    if (chatTypeEvents.length > 0) {
      const lastQuery = chatEvents.find((e, i) => e.type === 'CHAT_QUERY' && i === lastQueryIndex)
      const lastQueryTime = lastQuery?.timestamp || 0
      
      const typingAfterQuery = chatTypeEvents.filter(e => e.timestamp > lastQueryTime)
      const lastTyping = typingAfterQuery[typingAfterQuery.length - 1]
      
      if (lastTyping) {
        currentInput = lastTyping.payload?.text || ''
      }
    } else if (chatTypeEvents.length > 0) {
      currentInput = chatTypeEvents[chatTypeEvents.length - 1]?.payload?.text || ''
    }

    // AI processing state - after CHAT_QUERY, before CHAT_RESPONSE
    const lastChatEvent = [...chatEvents]
      .reverse()
      .find(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
    
    const isAiProcessing = lastChatEvent?.type === 'CHAT_QUERY'

    // Pages with a written explanation next to the code (Reasoning Gap) log it alongside
    // the code snapshot; null when no event has carried one yet.
    const lastExplanationEvent = [...visibleEvents]
      .reverse()
      .find(e => typeof e.payload?.explanation === 'string')
    const explanation = lastExplanationEvent ? lastExplanationEvent.payload.explanation : null

    // The most recent change at this point (code, explanation or a chat message), with what
    // came before it, for change highlighting. Snapshot-based pages (Reasoning Gap) save the
    // code and explanation after each pause in typing, so a change is everything typed since
    // the previous pause.
    const steps = changeSteps(visibleEvents, starterCode)
    const lastChange = steps.length ? steps[steps.length - 1] : null

    // Adapter-only state (see replayAnnotations.js): what covers or tops the editor, and
    // whether the student had the page hidden or unfocused.
    const latestNotice = latestPayload(visibleEvents, REPLAY_EVENTS.NOTICE)
    const notice = latestNotice && latestNotice.mode !== 'none' ? latestNotice : null
    const latestAway = latestPayload(visibleEvents, REPLAY_EVENTS.AWAY)
    const away = latestAway?.away ? latestAway : null

    return {
      mousePos,
      isClicking,
      code,
      activeFile,
      ideSplitPercent,
      jsViewSplitPercent,
      terminalOutput,
      isRunning,
      testResults,
      messages,
      currentInput,
      isAiProcessing,
      explanation,
      lastChange,
      notice,
      away,
      visibleEvents,
    }
  }, [events, currentTime, startTime, starterCode])
}